import {
  PropertyTranslator,
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  INamedEntityLabels,
  IPermissionedEntityLabels,
  INamedEntityParametersTranslator,
} from "../GenericTranslator";
import {
  DefaultHierarchyOrderTypeConsts,
  GenericEntityOrderBy,
  IEntityMinimalModel,
  IGenericRequestParameters,
  IHierarchyTypeRead,
  IModelWithIntId,
  INamedEntity,
  INamedEntityParameters,
  IPaginationParameters,
  IPermissionedEntity,
  NamedEntityOrderBy,
} from "../GenericTypes";

export const CustomTypeEntityTypeOptions = ["Dataset", "InventoryItem", "Person", "Project", "Sample"] as const;

export type CustomTypeEntityType = (typeof CustomTypeEntityTypeOptions)[number];

export interface CustomTypeBase extends IModelWithIntId, INamedEntity, IPermissionedEntity, IHierarchyTypeRead {
  description: string;
  inventoryDescription?: string | null;
  entityType: CustomTypeEntityType;
  isEnabled: boolean;
  hasRestrictedAddPermission: boolean;
  hasRestrictedEditPermission: boolean;
  hasRestrictedReadPermission: boolean;
  templateVersion?: string;
  integrations?: string[];
}

export const CustomTypeBaseFieldLabels: PropertyTranslator<CustomTypeBase> & { layout: string } = {
  id: "Custom type ID",
  ...INamedEntityLabels,
  ...IPermissionedEntityLabels,
  description: "Description",
  entityType: "Entity type",
  isEnabled: "Is enabled",
  rootHierarchy: "Root hierarchy",
  isHierarchyRoot: "Enable hierarchy",
  layout: "Layout",
  inventoryDescription: "Inventory description",
  hasRestrictedAddPermission: "Has restricted add permission",
  hasRestrictedEditPermission: "Has restricted edit permission",
  hasRestrictedReadPermission: "Has restricted read permission",
  inventoryName: "Inventory name",
  parentTypes: "Parent types",
  templateVersion: "Template Version",
  integrations: "Required for integration",
} as const;

export interface CustomTypeBaseSection extends INamedEntity {
  isFolded?: boolean;
  isHeadless?: boolean;
}

export interface CustomTypeSuggestions extends IEntityMinimalModel<CustomTypeBase["id"]> {
  inventoryName?: CustomTypeBase["inventoryName"];
  isHierarchyRoot?: CustomTypeBase["isHierarchyRoot"];
}

export interface CustomTypeBaseFilters<SortingOptions>
  extends IGenericRequestParameters<CustomTypeBase, SortingOptions>,
    IPaginationParameters,
    INamedEntityParameters {
  excludeDisabled?: boolean | null;
  isEnabled?: boolean | null;
  customFieldIds?: number[] | null;
  entityTypes?: CustomTypeEntityType[] | null;
  extendSearchToInventoryItems?: boolean | null;
  parentTypeIds?: number[] | null;
  hasRestrictedAddPermission?: boolean | null;
  hasRestrictedEditPermission?: boolean | null;
  hasRestrictedReadPermission?: boolean | null;
  rootHierarchyIds?: number[] | null;
  isInventory?: boolean | null;
  isHierarchyRoot?: boolean | null;
  excludeHierarchyChildren?: boolean | null;
  inventoryNames?: string[] | null;
  excludeNonInventories?: boolean | null;
}

export const CustomTypeBaseSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...DefaultHierarchyOrderTypeConsts,
] as const;
export type CustomTypeBaseSortingOptions = (typeof CustomTypeBaseSortingOptionsConsts)[number];

export const CustomTypeBaseFiltersTranslator: PropertyTranslator<CustomTypeBaseFilters<CustomTypeBaseSortingOptions>> =
  {
    ...IGenericRequestParametersTranslator,
    ...IPaginationParametersTranslator,
    ...INamedEntityParametersTranslator,
    customFieldIds: "Custom field ID(s)",
    entityTypes: "Entity Type(s)",
    isEnabled: "Is enabled",
    excludeDisabled: "Exclude disabled",
    extendSearchToInventoryItems: "Extended search (inventory items)",
    parentTypeIds: "Valid as parent for types",
    rootHierarchyIds: "Root hierarchy IDs",
    isInventory: "Is root",
    isHierarchyRoot: "Is hierarchical",
    excludeHierarchyChildren: "Exclude hierarchy children",
    inventoryNames: "Inventory name(s)",
    excludeNonInventories: "Exclude non-inventories",
    hasRestrictedAddPermission: "Has restricted add permission",
    hasRestrictedEditPermission: "Has restricted edit permission",
    hasRestrictedReadPermission: "Has restricted read permission",
  } as const;

export const CustomTypeDataTypeUtils = {
  CanDefineHierarchy: (entityType: CustomTypeEntityType) => {
    switch (entityType) {
      case "InventoryItem":
        return true;
      default:
        return false;
    }
  },
};
